import cn from "classnames"
import { useTransition } from "react"

import { Drawer, Tabs } from "@/atoms"
import { useLogger } from "@/hooks/useLogger"
import {
  decodeParams,
  encodeParams
} from "@/organisms/Drawers/applications/common/common"
import { useStateDrawer } from "@/organisms/Drawers/hooks/useStateDrawer"
import { ErrorBoundary } from "@/organisms/ErrorBoundary"
import { useKai } from "@/organisms/KaiChat/hook/useKai"
import { TabsTemplate } from "@/templates/TabsTemplate"
import { getIdsDrawer, useDrawer } from "../../hooks/useDrawer"
import { useTabs } from "../../hooks/useTabs"
import { ItemsTabs } from "../ItemsTabs"

import styles from "./TabsDrawerTemplate.module.scss"

import type { TabsDrawerTemplateComponent } from "."

export const TabsDrawerTemplate: TabsDrawerTemplateComponent = (props) => {
  const {
    tabs,
    open,
    titleProps,
    Extra,
    Below,
    classes = {},
    decode = decodeParams,
    encode = encodeParams,
    ...drawerProps
  } = props
  const drawer = useDrawer(drawerProps.id)
  const attr = { decode, encode, drawer }
  const { logger, EVENTS } = useLogger()
  const tabsState = useTabs(tabs, attr, props)
  const drawerState = useStateDrawer(attr, props)
  const [{ activeTab }, actions] = tabsState
  const items = ItemsTabs(tabsState, drawerState, props)
  const { Title, minWidth, width = 900 } = props
  const { close } = drawer
  const [, startTransition] = useTransition()
  const kai = useKai()
  // A drawer without tabs shows its children instead
  const displayTabs = tabs.length > 0 && !!drawerState.drawerId
  const onClose = () => {
    if (drawerState.drawerId) close()
  }
  const onUpdateDrawerState = (currentActiveTab = activeTab) => {
    const bodyElement = document.getElementsByClassName("ant-drawer-body")[0]

    if (bodyElement) {
      bodyElement.scrollTop = 0
      bodyElement.scrollLeft = 0
    }

    queueMicrotask(() => {
      const { drawerId } = drawerState

      if (drawerId)
        kai.updateDrawerState({ drawerId, activeTab: currentActiveTab })
    })
  }
  const defaultActiveKey = drawerState.record?.meta?.__defaultActiveKey

  return (
    <ErrorBoundary
      placement="DRAWER"
      width={width}
      logger={logger}
      onClose={onClose}
    >
      <Drawer
        destroyOnHidden
        resizable
        getContainer={false}
        rootStyle={{ position: "absolute" }}
        open={drawerState.open}
        size={width}
        minWidth={minWidth}
        className={cn(
          styles.drawer,
          kai.open && styles.drawerKai,
          classes.drawer
        )}
        onClose={onClose}
        title={Title && <Title {...drawerState} {...titleProps} />}
        afterOpenChange={(open) => {
          if (open)
            logger.info(EVENTS.ANALYTIC_EVENTS.DRAWER.OPENED, {
              name: props.name,
              id: drawerState.record?.id
            })

          // A controlled drawer follows the URL's active-tab param, so a
          // requested default tab is written there once on open.
          if (
            open &&
            props.controlledActiveKey &&
            defaultActiveKey &&
            !drawer.searchParams.has(drawer.activeTabFieldName)
          )
            actions.onChangeActiveKey(defaultActiveKey)

          if (open) {
            const idsDrawers = getIdsDrawer(drawer.searchParams)

            if (idsDrawers.length > 3) {
              startTransition(() => {
                props.requestCloseDrawer?.(idsDrawers[0])
              })
            }

            actions.forceRender()
          }

          if (!open) close()

          onUpdateDrawerState()
        }}
        {...drawerProps}
      >
        {displayTabs && (
          <TabsTemplate placement="drawer">
            {Extra && <Extra {...drawerState} />}
            <Tabs
              defaultActiveKey={defaultActiveKey}
              items={items}
              renderTabBar={
                Below
                  ? (tabBarProps, DefaultTabBar) => (
                      <>
                        <DefaultTabBar {...tabBarProps} />
                        <Below {...drawerState} actions={actions} />
                      </>
                    )
                  : undefined
              }
              onChange={(nextActiveTab) => {
                const { name } = props

                logger.info(EVENTS.ANALYTIC_EVENTS.DRAWER.CHANGE_TAB, {
                  name,
                  id: drawerState.record?.id,
                  tab: nextActiveTab
                })

                if (props.controlledActiveKey) {
                  actions.onChangeActiveKey(nextActiveTab)
                }

                onUpdateDrawerState(nextActiveTab)
              }}
              {...(props.controlledActiveKey ? { activeKey: activeTab } : {})}
            />
          </TabsTemplate>
        )}
        {!tabs.length && props.children}
      </Drawer>
    </ErrorBoundary>
  )
}
